import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AccessStep from "../components/AccessStep.jsx";
import SupportFooter from "../components/SupportFooter.jsx";
import AdminChoiceStep from "../components/AdminChoiceStep.jsx";
import CheckoutStep from "../components/CheckoutStep.jsx";
import IntroStep from "../components/IntroStep.jsx";
import ManualReviewStep from "../components/ManualReviewStep.jsx";
import ProductSelectionStep from "../components/ProductSelectionStep.jsx";
import SuccessModal from "../components/SuccessModal.jsx";
import WizardFrame from "../components/WizardFrame.jsx";
import {
  getAllowedCountries,
  getEligibleRewards,
  submitRedemption,
  validateDriverCode
} from "../services/mockRedemptionApi.js";
import { initialForm, wizardSteps } from "../utils/constants.js";
import {
  buildSelectedProducts,
  buildSelectedProduct,
  canSubmitRedemption,
  filterLandstarProductsForSelection,
  getLandstarRequiredProductIds,
  getLandstarSelectionLimit,
  getLandstarSelectionRule,
  getLandstarSelectionValidationMessage,
  getDefaultProductSelection,
  isLandstarSelectionValid,
  normalizeDriverCode,
  normalizeLandstarEmail
} from "../utils/helpers.js";
import { LANDSTAR_IMPORT_SESSION_KEY } from "../utils/constants.js";
import "../styles/index.css";

function LandstarSafetyRedemptionPage() {
  const navigate = useNavigate();
  const wizardRef = useRef(null);
  const [currentStep, setCurrentStep] = useState("intro");
  const [maxStepReached, setMaxStepReached] = useState(0);
  const [driverCode, setDriverCode] = useState("");
  const [driverProfile, setDriverProfile] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSelections, setProductSelections] = useState({});
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [validatingAccess, setValidatingAccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [validationOrderStatus, setValidationOrderStatus] = useState(null);

  const allowedCountries = getAllowedCountries();
  const selectedCountry =
    allowedCountries.find((country) => country.code === form.country) || allowedCountries[0];
  const selectedProducts = buildSelectedProducts(
    availableProducts,
    productSelections,
    selectedProductIds
  );
  const selectionLimit = getLandstarSelectionLimit(driverProfile);
  const canSubmit = canSubmitRedemption({
    driverProfile,
    selectedProducts,
    form
  });

  const stepIndexById = wizardSteps.reduce((accumulator, step, index) => {
    accumulator[step.id] = index;
    return accumulator;
  }, {});

  const goToStep = (stepId) => {
    const nextIndex = stepIndexById[stepId];

    if (nextIndex === undefined || nextIndex > maxStepReached) {
      return;
    }

    setCurrentStep(stepId);
  };

  const advanceToStep = (stepId) => {
    const nextIndex = stepIndexById[stepId] ?? 0;
    setMaxStepReached((current) => Math.max(current, nextIndex));
    setCurrentStep(stepId);
  };

  const handleAccessSubmit = async (event) => {
    event.preventDefault();

    try {
      setValidatingAccess(true);
      setError("");
      setValidationOrderStatus(null);
      const normalizedCode = driverCode.trim();
      const normalizedEmail = normalizeLandstarEmail(form.email);
      const driver = await validateDriverCode(normalizedCode, normalizedEmail);
      const rewards = Array.isArray(driver?.products)
        ? driver.products
        : await getEligibleRewards(driver);
      const eligibleRewards = filterLandstarProductsForSelection(driver, rewards);
      const requiredProductIds = getLandstarRequiredProductIds(driver, eligibleRewards);

      setDriverProfile(driver);
      setAvailableProducts(eligibleRewards);
      setProductSelections({});
      setSelectedProductIds(requiredProductIds);
      setForm((current) => ({
        ...current,
        driverCode: normalizedCode,
        email: normalizedEmail
      }));

      if (driver.canImportEligibility) {
        window.sessionStorage.setItem(
          LANDSTAR_IMPORT_SESSION_KEY,
          JSON.stringify({
            driverCode: normalizedCode,
            email: normalizedEmail
          })
        );
        setMaxStepReached((current) => Math.max(current, stepIndexById.access));
        setCurrentStep("admin");
        return;
      }

      setMaxStepReached((current) => Math.max(current, stepIndexById.rewards));
      setCurrentStep(driver.rewardGroup === "manual" ? "manual" : "rewards");
    } catch (requestError) {
      setValidationOrderStatus(requestError.order || null);
      setError(requestError.message || "Unable to validate driver code.");
    } finally {
      setValidatingAccess(false);
    }
  };

  const handleSelectionChange = (productId, field, value) => {
    const normalizedProductId = String(productId);

    setProductSelections((current) => ({
      ...current,
      [normalizedProductId]: {
        ...current[normalizedProductId],
        [field]: value
      }
    }));
  };

  const handleSelectProduct = (product, selection) => {
    if (getLandstarSelectionRule(driverProfile).mode === "required_kit") {
      return;
    }

    const normalizedProductId = String(product.id);
    const normalizedProduct = buildSelectedProduct(product, selection);

    setProductSelections((current) => ({
      ...current,
      [normalizedProductId]: {
        ...current[normalizedProductId],
        size: normalizedProduct.selectedSize,
        color: normalizedProduct.selectedColor,
        personalization: normalizedProduct.personalization || ""
      }
    }));

    setSelectedProductIds((current) => {
      const alreadySelected = current.includes(normalizedProductId);

      if (alreadySelected) {
        return current.filter((productId) => productId !== normalizedProductId);
      }

      if (selectionLimit === 1) {
        return [normalizedProductId];
      }

      return [...current, normalizedProductId];
    });

    setError("");
  };

  const handleContinueToCheckout = () => {
    if (!isLandstarSelectionValid(driverProfile, selectedProducts)) {
      setError(getLandstarSelectionValidationMessage(driverProfile));
      return;
    }

    const hasMissingSelection = selectedProducts.some((product) => {
      const selection = getDefaultProductSelection(product, {
        size: product.selectedSize,
        color: product.selectedColor,
        personalization: product.personalization,
      });

      return !selection.size || !selection.color;
    });

    if (hasMissingSelection) {
      setError("Choose both a size and color for each selected reward to continue.");
      return;
    }

    advanceToStep("checkout");
    setError("");
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "country" ? { state: "" } : {})
    }));
  };

  const handleOpenImportPage = () => {
    navigate("/landstar-safety-redemption/import-eligibility", {
      state: {
        email: form.email,
        driverCode: form.driverCode || driverCode.trim()
      }
    });
  };

  const handleContinueFromAdminChoice = () => {
    setCurrentStep(driverProfile?.rewardGroup === "manual" ? "manual" : "rewards");
    setMaxStepReached((current) => Math.max(current, stepIndexById.rewards));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setSubmissionError("");
    setSubmission({
      products: selectedProducts,
      form,
      driver: driverProfile
    });
  };

  const handleConfirmSubmit = async () => {
    if (!submission) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmissionError("");
      const response = await submitRedemption(submission);
      setSubmission(null);
      navigate(
        `/landstar-safety-redemption/confirmation?submissionId=${encodeURIComponent(
          response.confirmationId
        )}`,
        {
          state: {
            confirmationId: response.confirmationId,
            stageStatus: response.stageStatus,
            payload: response.payload
          }
        }
      );
    } catch (submitError) {
      setSubmissionError(submitError.message || "Unable to submit redemption.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetFlow = () => {
    setCurrentStep("intro");
    setMaxStepReached(0);
    setDriverCode("");
    setDriverProfile(null);
    setAvailableProducts([]);
    setProductSelections({});
    setSelectedProductIds([]);
    setForm(initialForm);
    setError("");
    setSubmissionError("");
    setValidatingAccess(false);
    setSubmitting(false);
    setSubmission(null);
    setValidationOrderStatus(null);
    window.sessionStorage.removeItem(LANDSTAR_IMPORT_SESSION_KEY);
  };

  const handleCloseSubmissionModal = () => {
    if (submitting) {
      return;
    }

    if (submission?.confirmationId) {
      return;
    }

    setSubmission(null);
    setSubmissionError("");
  };

  const railStep =
    currentStep === "manual" || currentStep === "admin" ? "access" : currentStep;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    wizardRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [currentStep]);

  const wizardBody = (() => {
    if (currentStep === "intro") {
      return <IntroStep onBegin={() => advanceToStep("access")} />;
    }

    if (currentStep === "manual") {
      return (
        <ManualReviewStep
          driverProfile={driverProfile}
          onBack={() => setCurrentStep("access")}
          onReset={resetFlow}
        />
      );
    }

    if (currentStep === "admin") {
      return (
        <AdminChoiceStep
          driverProfile={driverProfile}
          existingOrder={driverProfile?.existingOrder || null}
          onContinueRedemption={handleContinueFromAdminChoice}
          onOpenImport={handleOpenImportPage}
          onBack={() => setCurrentStep("access")}
        />
      );
    }

    if (currentStep === "rewards") {
      return (
        <ProductSelectionStep
          driverProfile={driverProfile}
          availableProducts={availableProducts}
          selectedProducts={selectedProducts}
          selectedProductIds={selectedProductIds}
          productSelections={productSelections}
          selectionLimit={selectionLimit}
          onSelectionChange={handleSelectionChange}
          onSelect={handleSelectProduct}
          onBack={() => setCurrentStep("access")}
          onContinue={handleContinueToCheckout}
          error={error}
        />
      );
    }

    if (currentStep === "checkout") {
      return (
        <CheckoutStep
          form={form}
          selectedCountry={selectedCountry}
          allowedCountries={allowedCountries}
          selectedProducts={selectedProducts}
          driverProfile={driverProfile}
          canSubmit={canSubmit}
          submitting={submitting}
          error={error}
          onFormChange={handleFormChange}
          onBack={() => setCurrentStep("rewards")}
          onSubmit={handleSubmit}
        />
      );
    }

    return (
      <AccessStep
        driverCode={driverCode}
        onDriverCodeChange={(value) => setDriverCode(normalizeDriverCode(value))}
        email={form.email}
        onEmailChange={(value) =>
          setForm((current) => ({
            ...current,
            email: normalizeLandstarEmail(value),
          }))
        }
        validating={validatingAccess}
        onSubmit={handleAccessSubmit}
        error={error}
        orderStatus={validationOrderStatus}
      />
    );
  })();

  return (
    <div className="landstar-page-shell">
      <WizardFrame
        title="Landstar Safety Awards"
        steps={wizardSteps}
        currentStep={railStep}
        maxStepReached={maxStepReached}
        onStepSelect={goToStep}
        wizardRef={wizardRef}
      >
        {wizardBody}
      </WizardFrame>

      <SupportFooter />

      <SuccessModal
        submission={submission}
        error={submissionError}
        submitting={submitting}
        onConfirm={handleConfirmSubmit}
        onClose={handleCloseSubmissionModal}
        onReset={resetFlow}
      />
    </div>
  );
}

export default LandstarSafetyRedemptionPage;
